import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Heading3, Paragraph, Subheading, Table } from '@treams/treams-ui';

import { DashboardEngagementLoading } from './DashboardEngagementLoading';
import { DashboardEngagementNoDataAvailable } from './DashboardEngagementNoDataAvailable';
import EngagementQuestion from './metrics/EngagementQuestion';
import { EngagementQuestionsExportButton } from './metrics/EngagementQuestionsExportButton';

function DashboardEngagementQuestionSubcategory(props: {
	subcategory: graphql.EngagementSubcategory;
	questions: (
		| graphql.AnalyticsEngagementQuestionScoreTrend
		| graphql.AnalyticsEngagementOpenQuestion
	)[];
	first?: boolean;
}) {
	const { localize } = useLocalize();
	const el = useRef<HTMLDivElement>(null);

	const { location } = useHistory();

	const selectedID = location.hash.substring(1);
	const isSelected = selectedID === props.subcategory.id;

	useEffect(() => {
		if (isSelected) {
			el.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [isSelected]);

	const sorted = _.sortBy(props.questions, ({ question }) => localize(question.title).text);

	// Negative margin styling results in consistent scroll-to behavior
	return (
		<Box pt={4} mt={props.first ? -4 : 0} ref={el}>
			<Table>
				<Table.Header>
					<Box px={3} py={2}>
						<Subheading>{localize(props.subcategory.title).text}</Subheading>
						{props.subcategory.description && props.subcategory.description.length > 0 && (
							<Paragraph mt={1}>{localize(props.subcategory.description).text}</Paragraph>
						)}
					</Box>
				</Table.Header>
				<Box>
					{sorted.map((question, i) => (
						<EngagementQuestion key={question.question.id} question={question} first={i === 0} />
					))}
				</Box>
			</Table>
		</Box>
	);
}

function DashboardEngagementQuestionCategory(props: {
	category: graphql.EngagementCategory;
	questions: (
		| graphql.AnalyticsEngagementQuestionScoreTrend
		| graphql.AnalyticsEngagementOpenQuestion
	)[];
}) {
	const { localize } = useLocalize();

	const subcategories = new Map(
		props.questions.map(({ question }) => [question.subcategory.id, question.subcategory]),
	);

	const grouped = _.groupBy(props.questions, ({ question }) => question.subcategory.id);
	const groups = _.sortBy(
		Object.keys(grouped),
		(id) => localize(subcategories.get(id)?.title ?? [])?.text,
	);

	return (
		<>
			<Heading3 mb={3}>{localize(props.category.title).text}</Heading3>
			{groups.map((id, i) => {
				const subcategory = subcategories.get(id);

				if (!subcategory) {
					return <DashboardEngagementNoDataAvailable key={id} />;
				}

				return (
					<DashboardEngagementQuestionSubcategory
						key={id}
						subcategory={subcategory}
						questions={grouped[id]}
						first={i == 0}
					/>
				);
			})}
		</>
	);
}

function EngagementQuestions(props: {
	data:
		| graphql.WorkspaceEngagementQuestionsQuery
		| graphql.GroupEngagementQuestionsQuery
		| graphql.EngagementOfficerQuestionsQuery;
	filters: graphql.AnalyticsFilters;
}) {
	const { localize } = useLocalize();

	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
				? props.data.groupAnalytics
				: props.data.engagementAnalytics;

	const hasData =
		data.engagementQuestionsTrend.length > 0 || data.engagementOpenQuestions.length > 0;

	const questions = (
		data.engagementQuestionsTrend as (
			| graphql.AnalyticsEngagementQuestionScoreTrend
			| graphql.AnalyticsEngagementOpenQuestion
		)[]
	).concat(data.engagementOpenQuestions as graphql.AnalyticsEngagementOpenQuestion[]);

	const categories = new Map(
		questions.map(({ question }) => [question.category.id, question.category]),
	);
	const grouped = _.groupBy(questions, ({ question }) => question.category.id);
	const groups = _.sortBy(Object.keys(grouped), (id) => localize(categories.get(id)!.title).text);

	if (!groups || groups.length === 0) {
		return <DashboardEngagementNoDataAvailable />;
	}

	return (
		<>
			{hasData && (
				<EngagementQuestionsExportButton
					grouped={grouped}
					categories={categories}
					filters={props.filters}
					groups={groups}
				/>
			)}
			{groups.map((id) => (
				<Box key={id} mb={5}>
					<DashboardEngagementQuestionCategory
						category={categories.get(id)!}
						questions={grouped[id]}
					/>
				</Box>
			))}
		</>
	);
}

function WorkspaceQuestions(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useWorkspaceEngagementQuestionsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementQuestions data={query.data} filters={props.filters} />;
}

function GroupQuestions(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useGroupEngagementQuestionsQuery({
		variables: {
			filters: props.filters,
		},
		skip: props.filters.groupIDs?.length === 0,
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementQuestions data={query.data} filters={props.filters} />;
}

function EngagementOfficerQuestions(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useEngagementOfficerQuestionsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementQuestions data={query.data} filters={props.filters} />;
}

export default function DashboardEngagementQuestions(props: {
	filters: graphql.AnalyticsFilters;
	headless?: boolean;
}) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementOfficerQuestions filters={props.filters} />;
	}

	if (userRoles.isWorkspaceSuperuser) {
		return <WorkspaceQuestions filters={props.filters} />;
	}

	return <GroupQuestions filters={props.filters} />;
}
